import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Trash2, Save, XCircle, RotateCcw } from "lucide-react";
import { CHANNELS } from "@/lib/channels";

const emptyForm = {
  property_booked: "",
  status: "UPCOMING",
  check_in: "",
  check_out: "",
  ota_or_direct: "Direct Bookings",
  booked_by: "",
  guests: 1,
  nights: 1,
  nightly_rate: 0,
  cleaning_fee: 0,
  commission_fee: 0,
  vat: 0,
  host_payout: 0,
  security_deposit: 0,
  date_paid: "",
  payment_status: "PENDING",
  payment_remarks: "",
  company_name: "",
  reservation_code: "",
  contact_no: "",
  email_address: "",
  tc_accepted: false,
  notes: "",
  cancellation_reason: "",
  cancelled_at: "",
  expected_payout_date: "",
};

export default function BookingForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { company } = useCompany();
  const { user: authUser } = useAuth();
  const isAdmin = (authUser?.role || "ADMIN").toUpperCase() === "ADMIN";
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [properties, setProperties] = useState([]);
  const [customProp, setCustomProp] = useState(false);

  useEffect(() => {
    api.get("/properties").then((r) => setProperties(r.data));
  }, [company]);

  useEffect(() => {
    if (id) {
      api.get(`/bookings/${id}`).then((r) => setForm({ ...emptyForm, ...r.data }));
    }
  }, [id]);

  const update = (k, v) => setForm((f) => {
    const next = { ...f, [k]: v };
    // When status is VACANT, channel auto-set to "Not Applicable" to avoid data confusion
    if (k === "status" && v === "VACANT") {
      next.ota_or_direct = "Not Applicable";
    }
    // Payment status → per-status date rules (no other dates carry over)
    if (k === "payment_status") {
      if (v === "PENDING") {
        // Pending: date_paid blank; expected_payout_date stays manual
        next.date_paid = "";
      } else if (v === "NOT APPLICABLE") {
        // Not applicable: both blank
        next.date_paid = "";
        next.expected_payout_date = "";
      }
      // PAID and REFUNDED: keep both manual (no automatic changes)
    }
    return next;
  });

  useEffect(() => {
    if (form.check_in && form.check_out) {
      const d1 = new Date(form.check_in);
      const d2 = new Date(form.check_out);
      const diff = Math.round((d2 - d1) / (1000 * 60 * 60 * 24));
      if (diff >= 0) setForm((f) => ({ ...f, nights: diff }));
    }
    // eslint-disable-next-line
  }, [form.check_in, form.check_out]);

  // Auto-calc host payout: nightly rate + cleaning fee − commission − VAT
  useEffect(() => {
    const payout =
      (Number(form.nightly_rate) || 0) +
      (Number(form.cleaning_fee) || 0) -
      (Number(form.commission_fee) || 0) -
      (Number(form.vat) || 0);
    setForm((f) => ({ ...f, host_payout: Number(payout.toFixed(2)) }));
    // eslint-disable-next-line
  }, [form.nightly_rate, form.cleaning_fee, form.commission_fee, form.vat]);

  const save = async () => {
    if (!isAdmin) { toast.error("View-only role — cannot save"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      ["guests", "nights"].forEach((k) => (payload[k] = parseInt(payload[k]) || 0));
      ["nightly_rate", "cleaning_fee", "commission_fee", "vat", "host_payout", "security_deposit"].forEach(
        (k) => (payload[k] = parseFloat(payload[k]) || 0)
      );
      ["check_in", "check_out", "date_paid", "cancelled_at", "expected_payout_date"].forEach((k) => {
        if (!payload[k]) payload[k] = null;
      });

      if (id) {
        await api.put(`/bookings/${id}`, payload);
        toast.success("Booking updated");
      } else {
        await api.post("/bookings", payload);
        toast.success("Booking created");
      }
      navigate("/bookings");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const confirmCancel = async () => {
    if (!id) return;
    try {
      await api.post(`/bookings/${id}/cancel`, { reason: cancelReason });
      toast.success("Booking cancelled");
      setCancelOpen(false);
      navigate("/bookings");
    } catch (e) {
      toast.error("Cancel failed");
    }
  };

  const reinstate = async () => {
    if (!id) return;
    const payload = { ...form, status: "UPCOMING", cancellation_reason: null, cancelled_at: null };
    ["guests", "nights"].forEach((k) => (payload[k] = parseInt(payload[k]) || 0));
    ["nightly_rate", "cleaning_fee", "commission_fee", "vat", "host_payout", "security_deposit"].forEach(
      (k) => (payload[k] = parseFloat(payload[k]) || 0)
    );
    ["check_in", "check_out", "date_paid"].forEach((k) => {
      if (!payload[k]) payload[k] = null;
    });
    await api.put(`/bookings/${id}`, payload);
    toast.success("Booking reinstated");
    navigate("/bookings");
  };

  const remove = async () => {
    if (!id) return;
    if (!confirm("Permanently delete this booking? Prefer 'Cancel booking' to keep records.")) return;
    await api.delete(`/bookings/${id}`);
    toast.success("Booking deleted");
    navigate("/bookings");
  };

  const isCancelled = form.status === "CANCELLED";

  return (
    <div className="p-4 sm:p-6 lg:p-10 space-y-6 lg:space-y-8 max-w-6xl">
      <button
        onClick={() => navigate("/bookings")}
        className="flex items-center gap-2 text-sm text-slate-600 hover:text-blue-900 label-eyebrow transition-colors"
        data-testid="back-button"
      >
        <ArrowLeft className="w-4 h-4" /> Back to bookings
      </button>

      <div>
        <div className="label-eyebrow">{id ? "Edit" : "New"}</div>
        <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight mt-2 text-slate-900">
          {id ? "Edit Booking" : "New Booking"}
        </h1>
        <p className="text-sm text-slate-500 mt-2">
          Rates, fees, VAT & host payout are auto-computed as you type.
        </p>
      </div>

      {!isAdmin && (
        <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-xs text-amber-900" data-testid="viewer-readonly-banner">
          View-only role — you can browse this booking but cannot edit, cancel, or delete it.
        </div>
      )}

      {isCancelled && (
        <div className="bg-rose-50 border border-rose-200 rounded-sm p-4" data-testid="cancelled-banner">
          <div className="flex items-start gap-3">
            <XCircle className="w-5 h-5 text-rose-700 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-rose-900">This booking is cancelled</div>
              {form.cancellation_reason && (
                <div className="text-sm text-rose-800 mt-1">Reason: {form.cancellation_reason}</div>
              )}
              {form.cancelled_at && (
                <div className="text-xs text-rose-700 mt-1">Cancelled on {new Date(form.cancelled_at).toLocaleString()}</div>
              )}
            </div>
            {isAdmin && (
              <Button
                onClick={reinstate}
                variant="outline"
                className="rounded-sm border-rose-300 text-rose-800 hover:bg-rose-100"
                data-testid="reinstate-button"
              >
                <RotateCcw className="w-4 h-4 mr-2" /> Reinstate
              </Button>
            )}
          </div>
        </div>
      )}

      <Section title="Property & Dates">
        <Field label="Property Booked">
          {customProp || (form.property_booked && !properties.includes(form.property_booked)) ? (
            <div className="flex gap-2">
              <Input
                value={form.property_booked || ""}
                onChange={(e) => update("property_booked", e.target.value)}
                placeholder="Enter property name"
                data-testid="input-property"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => { setCustomProp(false); update("property_booked", ""); }}
                className="rounded-sm border-slate-300 h-10 shrink-0"
                data-testid="input-property-pick"
              >
                Pick
              </Button>
            </div>
          ) : (
            <Select
              value={form.property_booked || ""}
              onValueChange={(v) => {
                if (v === "__custom__") { setCustomProp(true); update("property_booked", ""); }
                else update("property_booked", v);
              }}
            >
              <SelectTrigger data-testid="input-property"><SelectValue placeholder={`Select from ${company}`} /></SelectTrigger>
              <SelectContent>
                {properties.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                {properties.length === 0 && (
                  <div className="px-3 py-2 text-xs text-slate-500">No properties for this company yet.</div>
                )}
                <SelectItem value="__custom__" data-testid="input-property-custom">+ Add new property…</SelectItem>
              </SelectContent>
            </Select>
          )}
        </Field>
        <Field label="Status">
          <Select value={form.status} onValueChange={(v) => update("status", v)}>
            <SelectTrigger data-testid="input-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["PRE-BOOKING", "ON-PROCESS", "UPCOMING", "CHECKED-IN", "CHECKED-OUT", "VACANT", "CANCELLED"].map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Channel">
          <Select value={form.ota_or_direct || "Direct Bookings"} onValueChange={(v) => update("ota_or_direct", v)}>
            <SelectTrigger data-testid="input-ota"><SelectValue placeholder="Select channel" /></SelectTrigger>
            <SelectContent>
              {CHANNELS.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Check-in date">
          <Input type="date" value={form.check_in || ""} onChange={(e) => update("check_in", e.target.value)} data-testid="input-checkin" />
        </Field>
        <Field label="Check-out date">
          <Input type="date" value={form.check_out || ""} onChange={(e) => update("check_out", e.target.value)} data-testid="input-checkout" />
        </Field>
        <Field label="Booked By">
          <Input value={form.booked_by || ""} onChange={(e) => update("booked_by", e.target.value)} data-testid="input-bookedby" />
        </Field>
      </Section>

      <Section title="Rates & Fees">
        <Field label="No. Guests">
          <Input type="number" value={form.guests} onChange={(e) => update("guests", e.target.value)} data-testid="input-guests" />
        </Field>
        <Field label="No. Nights (auto)">
          <Input type="number" value={form.nights} onChange={(e) => update("nights", e.target.value)} data-testid="input-nights" />
        </Field>
        <Field label="Nightly Rate (£)">
          <Input type="number" step="0.01" value={form.nightly_rate} onChange={(e) => update("nightly_rate", e.target.value)} data-testid="input-rate" />
        </Field>
        <Field label="Cleaning Fee (£)">
          <Input type="number" step="0.01" value={form.cleaning_fee} onChange={(e) => update("cleaning_fee", e.target.value)} data-testid="input-cleaning" />
        </Field>
        <Field label="Commission / Service Fee (£)">
          <Input type="number" step="0.01" value={form.commission_fee} onChange={(e) => update("commission_fee", e.target.value)} data-testid="input-commission" />
        </Field>
        <Field label="VAT 20% (£)">
          <Input type="number" step="0.01" value={form.vat} onChange={(e) => update("vat", e.target.value)} data-testid="input-vat" />
        </Field>
        <Field label="Host Payout (auto, £)">
          <Input type="number" step="0.01" value={form.host_payout} onChange={(e) => update("host_payout", e.target.value)} className="font-semibold text-blue-900" data-testid="input-payout" />
        </Field>
        <Field label="Security Deposit (£)">
          <Input type="number" step="0.01" value={form.security_deposit} onChange={(e) => update("security_deposit", e.target.value)} data-testid="input-deposit" />
        </Field>
      </Section>

      <Section title="Payment">
        <Field label="Payment Status">
          <Select value={form.payment_status} onValueChange={(v) => update("payment_status", v)}>
            <SelectTrigger data-testid="input-paymentstatus"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="PAID">Paid</SelectItem>
              <SelectItem value="PENDING">Pending</SelectItem>
              <SelectItem value="REFUNDED">Refunded</SelectItem>
              <SelectItem value="NOT APPLICABLE">Not Applicable</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Date Paid">
          {form.payment_status === "NOT APPLICABLE" || form.payment_status === "PENDING" ? (
            <div
              className="h-10 px-3 flex items-center rounded-sm border border-slate-200 bg-slate-50 text-slate-400 text-sm font-mono select-none"
              data-testid="input-datepaid"
              aria-disabled="true"
            >
              {form.payment_status === "NOT APPLICABLE" ? "N/A · xx/xx/xxxx" : "— (blank for Pending)"}
            </div>
          ) : (
            <Input
              type="date"
              value={form.date_paid || ""}
              onChange={(e) => update("date_paid", e.target.value)}
              data-testid="input-datepaid"
            />
          )}
        </Field>
        <Field label="Expected Payout Date">
          {form.payment_status === "NOT APPLICABLE" ? (
            <div
              className="h-10 px-3 flex items-center rounded-sm border border-slate-200 bg-slate-50 text-slate-400 text-sm font-mono select-none"
              data-testid="input-expected-payout"
              aria-disabled="true"
            >
              N/A · xx/xx/xxxx
            </div>
          ) : (
            <div className="flex gap-2">
              <Input
                type="date"
                value={form.expected_payout_date || ""}
                onChange={(e) => update("expected_payout_date", e.target.value)}
                data-testid="input-expected-payout"
              />
              {form.expected_payout_date && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => update("expected_payout_date", "")}
                  className="rounded-sm border-slate-300 h-10 shrink-0 text-slate-500 hover:text-rose-700"
                  data-testid="clear-expected-payout"
                  title="Clear expected payout date"
                >
                  Clear
                </Button>
              )}
            </div>
          )}
        </Field>
        <Field label="Remarks">
          <Textarea
            value={form.payment_remarks || ""}
            onChange={(e) => update("payment_remarks", e.target.value)}
            placeholder="Notes about this payment (e.g. bank ref, payer, delay reason)"
            className="min-h-[70px]"
            data-testid="input-payment-remarks"
          />
        </Field>
      </Section>

      <Section title="Guest Details">
        <Field label="Company Name">
          <Input value={form.company_name || ""} onChange={(e) => update("company_name", e.target.value)} data-testid="input-company" />
        </Field>
        <Field label="Reservation Code">
          <Input value={form.reservation_code || ""} onChange={(e) => update("reservation_code", e.target.value)} data-testid="input-reservation" />
        </Field>
        <Field label="Contact No.">
          <Input value={form.contact_no || ""} onChange={(e) => update("contact_no", e.target.value)} data-testid="input-contact" />
        </Field>
        <Field label="Email Address">
          <Input type="email" value={form.email_address || ""} onChange={(e) => update("email_address", e.target.value)} data-testid="input-email" />
        </Field>
        <Field label="Notes" full>
          <Textarea value={form.notes || ""} onChange={(e) => update("notes", e.target.value)} data-testid="input-notes" rows={3} />
        </Field>
        <div className="flex items-center gap-2 pt-2 md:col-span-3">
          <Checkbox id="tc" checked={!!form.tc_accepted} onCheckedChange={(v) => update("tc_accepted", !!v)} data-testid="input-tc" />
          <Label htmlFor="tc" className="text-sm cursor-pointer">Terms & Conditions accepted</Label>
        </div>
      </Section>

      <div className="flex flex-wrap items-center gap-2 sm:gap-3 pt-4 sticky bottom-0 bg-[#F1F5F9] py-4 -mx-4 sm:-mx-6 lg:-mx-10 px-4 sm:px-6 lg:px-10 border-t border-slate-200 z-20">
        {isAdmin && (
          <Button
            onClick={save}
            disabled={saving}
            className="rounded-sm bg-blue-900 hover:bg-blue-800 text-white active:scale-[0.98] flex-1 sm:flex-none"
            data-testid="save-booking-button"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving…" : id ? "Update booking" : "Create booking"}
          </Button>
        )}
        <Button variant="outline" onClick={() => navigate("/bookings")} className="rounded-sm">
          {isAdmin ? "Cancel" : "Back to bookings"}
        </Button>
        {id && !isCancelled && isAdmin && (
          <Button
            variant="outline"
            onClick={() => setCancelOpen(true)}
            className="rounded-sm border-amber-400 text-amber-800 hover:bg-amber-50 sm:ml-auto"
            data-testid="cancel-booking-button"
          >
            <XCircle className="w-4 h-4 mr-2" /> Cancel booking
          </Button>
        )}
        {id && isAdmin && (
          <Button
            variant="outline"
            onClick={remove}
            className="rounded-sm border-rose-300 text-rose-700 hover:bg-rose-50"
            data-testid="delete-booking-button"
          >
            <Trash2 className="w-4 h-4 mr-2" /> Delete
          </Button>
        )}
      </div>

      {/* Cancel dialog */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent className="rounded-sm" data-testid="cancel-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Cancel booking</DialogTitle>
            <DialogDescription>
              The record stays for accounting; status will change to CANCELLED. You can reinstate later.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="label-eyebrow">Reason (optional)</Label>
            <Textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="e.g. Guest changed plans, no-show, dispute…"
              rows={3}
              data-testid="cancel-reason-input"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCancelOpen(false)} className="rounded-sm">
              Keep booking
            </Button>
            <Button
              onClick={confirmCancel}
              className="rounded-sm bg-amber-700 hover:bg-amber-800 text-white"
              data-testid="confirm-cancel-button"
            >
              Cancel booking
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4 sm:p-6">
      <div className="label-eyebrow mb-4 sm:mb-5">{title}</div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">{children}</div>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={`space-y-1.5 ${full ? "md:col-span-2 lg:col-span-3" : ""}`}>
      <Label className="label-eyebrow">{label}</Label>
      {children}
    </div>
  );
}
